import React, { useCallback, useEffect, useRef } from 'react'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BULK_INVITE_ANALYTICS_EVENT } from 'src/app/BulkInvite/bulkInvite.constants'
import BulkInviteWizard from 'src/app/BulkInvite/BulkInviteWizard'
import {
  BulkInviteProvider,
  useBulkInvite,
} from 'src/app/BulkInvite/context/BulkInviteContext'
import FullscreenModalShell from 'src/app/BulkInvite/shells/FullscreenModalShell'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'

interface BulkInviteModalProps {
  /** Closes the modal — callers typically strip the `bulkInvite` query param. */
  onClose: () => void
}

/**
 * Lives inside the provider so it can read `step`/`submitted` and report which
 * step an admin abandoned on. Split out because BulkInviteModal itself renders
 * the provider and so sits outside its context.
 */
const BulkInviteModalContent: React.FC<BulkInviteModalProps> = ({
  onClose,
}) => {
  const strings = useBulkInviteStrings()
  const { analytics } = useAnalyticsContext()
  const { step, submitted } = useBulkInvite()

  useEffect(() => {
    analytics.page(AnalyticsPageName.BulkInviteModal)
  }, [analytics])

  // The unmount path below fires after render, so it cannot close over `step`
  // and `submitted` directly — it would see whatever values the effect was
  // created with. Mirror them into a ref instead.
  const latestRef = useRef({ step, submitted })
  latestRef.current = { step, submitted }

  // One-shot: the modal can leave via the close button AND then unmount, and
  // both paths funnel through here. Re-armed on mount so a StrictMode
  // double-invoke (or a reopened modal) still reports.
  const abandonReportedRef = useRef(false)

  const reportAbandon = useCallback(() => {
    // A successful send closes the modal too — that is not an abandon.
    if (abandonReportedRef.current || latestRef.current.submitted) return
    abandonReportedRef.current = true
    analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
      action: 'abandoned',
      step: latestRef.current.step,
    })
  }, [analytics])

  // The modal is mounted from `?bulkInvite=1`, so browser Back strips the
  // param and unmounts this component without ever calling onClose. Without an
  // unmount hook that ordinary exit is missing from the drop-off funnel.
  // `reportAbandon` depends only on the memoized `analytics`, so this effect
  // runs once per mount rather than per render.
  useEffect(() => {
    abandonReportedRef.current = false
    return reportAbandon
  }, [reportAbandon])

  const handleDismiss = useCallback(() => {
    reportAbandon()
    onClose()
  }, [reportAbandon, onClose])

  return (
    <FullscreenModalShell
      onClose={handleDismiss}
      ariaLabel={strings.shellTitle}
    >
      <BulkInviteWizard onClose={onClose} />
    </FullscreenModalShell>
  )
}

/**
 * The Bulk Invite (CSV) flow as a fullscreen modal layered over the live People
 * page. Mounted by PeopleMembers when `?bulkInvite=1` is present and the
 * `bulk_invite_csv` remote-config flag is enabled.
 */
const BulkInviteModal: React.FC<BulkInviteModalProps> = ({ onClose }) => (
  <BulkInviteProvider>
    <BulkInviteModalContent onClose={onClose} />
  </BulkInviteProvider>
)

export default BulkInviteModal
